import { Box, Stack, Typography } from '@mui/material';
import { Button, IconButton } from '@sweep-io/sweep-design';
import { ArrowLeft } from '@sweep-io/sweep-design/dist/icons/ArrowLeft';
import { useRoleManagerContext } from './RoleManagerContext';
import { CreateEditDialogHeader } from '../../common/create-edit-dialog/CreateEditDialogHeader';
import type { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { RoleGroupLevelType, RoleGroupType, RoleLevel } from '@server/role-group-interface';
import { useRoleGroups } from './useRoleManager';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { useCallback } from 'react';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

import { Plus } from '@sweep-io/sweep-design/dist/icons/Plus';

const createNewRole = () =>
  ({
    id: '',
    name: 'New role',
    type: RoleGroupType.USER_CREATED,
    description: '',
    roleGroupLevels: {
      type: RoleGroupLevelType.AccountBased,
      levels: {
        automations: RoleLevel.VIEWER,
        alerts: RoleLevel.VIEWER,
        assignments: RoleLevel.VIEWER,
        funnels: RoleLevel.VIEWER,
        rollups: RoleLevel.VIEWER,
        dedupMatching: RoleLevel.VIEWER,
        hubspot: RoleLevel.VIEWER,
        agentOperator: RoleLevel.VIEWER,
      },
    },
  }) as Partial<UserDefinedRoleGroupsInterface>;

export const RoleManagerHeader = () => {
  const { multiAccounts } = useFeatureToggle();
  const {
    goBack,
    editRole,
    hasChanges,
    headerText,
    setRoleManager,
    updateRole,
    initializeEditRole,
  } = useRoleManagerContext();

  const { isSavingUser } = useRoleGroups();
  const { openConfirm, onCancel, onConfirm } = useConfirm();

  const readonlyMode = editRole?.type === RoleGroupType.SYSTEM;

  const onDialogClose = useCallback(() => {
    goBack();
    onConfirm();
  }, [onConfirm, goBack]);

  const onNameChange = useCallback((value: string) => updateRole('name', value), [updateRole]);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      maxWidth="calc(100% - 8px)"
      height="42px"
      pb={1}
    >
      {!readonlyMode && editRole && (
        <CreateEditDialogHeader
          noCreateEditButtons
          value={editRole.name}
          placeholder={'Enter role name...'}
          onChange={onNameChange}
          onBackClick={async () => {
            if (!hasChanges && !isSavingUser) {
              goBack();
              return;
            }

            const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);

            if (isConfirmed) {
              onDialogClose();
            } else {
              onCancel();
            }
          }}
        />
      )}

      {(readonlyMode || !editRole) && (
        <>
          <Stack direction="row" alignItems="center" gap={1}>
            {multiAccounts ? (
              !editRole ? undefined : (
                <IconButton
                  variant="flat"
                  onClick={() => {
                    editRole && goBack();
                    !editRole && setRoleManager(false);
                  }}
                  size="small"
                >
                  <ArrowLeft size="medium" />
                </IconButton>
              )
            ) : (
              <IconButton
                variant="flat"
                onClick={() => {
                  editRole && goBack();
                  !editRole && setRoleManager(false);
                }}
                size="small"
              >
                <ArrowLeft size="medium" />
              </IconButton>
            )}

            <Typography variant={multiAccounts ? 'h3' : 'h2'}>{headerText}</Typography>
          </Stack>

          {!editRole && (
            <Box>
              <Button
                startIcon={Plus}
                size="small"
                onClick={() =>
                  initializeEditRole(createNewRole() as UserDefinedRoleGroupsInterface)
                }
              >
                Add role
              </Button>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};
